// YouTube URL helpers for the Voice Agent demo embed.

const ID_RE = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Extract a YouTube video id from any common URL shape:
 *  - https://www.youtube.com/watch?v=ID
 *  - https://youtu.be/ID
 *  - https://www.youtube.com/shorts/ID
 *  - https://www.youtube.com/embed/ID
 *  - https://www.youtube-nocookie.com/embed/ID
 * Returns null if it can't be parsed.
 */
export function extractYouTubeId(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  // Bare ID
  if (ID_RE.test(raw)) return raw;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      return ID_RE.test(id) ? id : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
      const v = u.searchParams.get("v");
      if (v && ID_RE.test(v)) return v;
      const parts = u.pathname.split("/").filter(Boolean);
      // /embed/ID, /shorts/ID, /live/ID, /v/ID
      if (parts.length >= 2 && ["embed", "shorts", "live", "v"].includes(parts[0])) {
        return ID_RE.test(parts[1]) ? parts[1] : null;
      }
    }
  } catch {
    /* not a URL */
  }
  return null;
}

export function isYouTubeSource(input: string | null | undefined): boolean {
  return extractYouTubeId(input) !== null;
}

export function buildYouTubeEmbedUrl(id: string, opts: { autoplay?: boolean; mute?: boolean } = {}): string {
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    // mute=1 is required for mobile browsers to honor autoplay=1 on YouTube embeds.
    // Users can unmute via the in-player control once playback starts. Pass
    // mute: false when playback starts from the visitor's own click.
    ...(opts.autoplay ? { autoplay: "1", ...(opts.mute === false ? {} : { mute: "1" }) } : {}),
  });
  return `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`;
}

export function youTubeThumbnail(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}
